import React, { useEffect, useLayoutEffect, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import EyeIcon from "../../assets/image/eye-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { formatTripDateTime } from "../../utils/formatDateTime";
import { formatInr } from "../../utils/formatAmount";
import { useBackToList } from "../../hooks/useBackToList";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const formatInrDisplay = (n) => formatInr(n, { symbolSpace: false });

const formatFuelEfficiency = (fuelForm) => {
  const raw = fuelForm?.efficiency ?? fuelForm?.efficency;
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(2)} km/l`;
};

const isNonEmptyObject = (o) =>
  o != null && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;

const tripStatusLabel = (status) => {
  const s = Number(status);
  if (s === 0) return "Started";
  if (s === 1) return "Ongoing";
  if (s === 2) return "Locked";
  if (s === 3) return "Completed";
  if (s === 4) return "Cancelled";
  if (typeof status === "string" && status.trim()) return status;
  return "—";
};

const resolvePersonName = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    const name =
      value.accountant_name ||
      value.name ||
      value.full_name ||
      value.username ||
      value.email ||
      value._id ||
      value.id ||
      "—";
    const employeeId =
      value.employee_id ||
      value.employeeId ||
      value.user_ref_id ||
      value.userRefId ||
      "";
    return employeeId && name !== "—" ? `${name} (${employeeId})` : name;
  }
  return String(value).trim() || "—";
};

const resolveCancelType = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    return value.type ?? value.name ?? value.cancel_type ?? value._id ?? "—";
  }
  return String(value).trim() || "—";
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "—";
};

const resolveDetailPeople = (source = {}) => {
  const accountantRef =
    source.accountant_id && typeof source.accountant_id === "object" ? source.accountant_id : {};
  const driverRef =
    source.driver_id && typeof source.driver_id === "object" ? source.driver_id : {};

  return {
    accountantName: firstNonEmpty(
      source.accountant_name,
      source.accountant_full_name,
      accountantRef.accountant_name,
      accountantRef.full_name,
      accountantRef.name
    ),
    accountantId: firstNonEmpty(
      source.employee_id,
      source.accountant_ref_id,
      source.accountant_employee_id,
      accountantRef.employee_id,
      accountantRef.accountant_ref_id,
      typeof source.accountant_id === "string" ? source.accountant_id : ""
    ),
    driverName: firstNonEmpty(
      source.driver_name,
      source.driver_full_name,
      driverRef.driver_name,
      driverRef.full_name,
      driverRef.name
    ),
    driverRefId: firstNonEmpty(
      source.driver_ref_id,
      source.driver_employee_id,
      driverRef.driver_ref_id,
      driverRef.employee_id,
      typeof source.driver_id === "string" ? source.driver_id : ""
    ),
  };
};

const mapExpenseRow = (e, index) => {
  const amount = e?.amount_spent ?? e?.amount ?? e?.spent_amount ?? e?.amountSpent;
  const desc = e?.description ?? e?.desc ?? e?.remark ?? "";
  const dt = e?.createdAt ?? e?.date_time ?? e?.datetime ?? e?.dateTime;
  const receipt =
    e?.receipt_image ??
    e?.recept_image ??
    e?.receipt ??
    e?.image_url ??
    e?.receipt_url ??
    "";
  return {
    id: e?._id ?? e?.id ?? `exp-${index}`,
    amountSpent: amount != null && amount !== "" ? formatInrDisplay(amount) : "—",
    accountantName: resolvePersonName(
      e?.accountant_id ??
        e?.accountant ??
        {
          accountant_name: e?.accountant_name,
          employee_id: e?.employee_id,
        }
    ),
    description: desc || "—",
    dateTime: formatTripDateTime(dt),
    receipt: typeof receipt === "string" ? receipt.trim() : "",
    rawDescription: desc || "—",
  };
};

const normalizeTripView = (raw) => {
  const d = raw?.data && typeof raw.data === "object" ? raw.data : raw;
  if (!d || typeof d !== "object") return null;

  const td = d.trip_details || {};
  const company = td.company_id || {};
  const companyLoc = company.company_location_id || {};
  const mipl = td.mipl_location_id || {};
  const vehicle = td.vehicle_id || {};
  const advance = d.trip_adavance_details || d.trip_advance_details || {};
  const debit = d.debit_note_details && typeof d.debit_note_details === "object" ? d.debit_note_details : {};
  const waive =
    d.waive_off_details && typeof d.waive_off_details === "object"
      ? d.waive_off_details
      : d.waiveOffDetails && typeof d.waiveOffDetails === "object"
        ? d.waiveOffDetails
        : {};
  const drivers = d.driver_details || {};
  const primary = drivers.primary_driver_details || {};
  const secondary = drivers.secondary_driver_details || {};
  const cancelledBy = td.trip_cancelled_by_id ?? d.trip_cancelled_by_id;
  const cancelType = td.trip_cancel_type_id ?? d.trip_cancel_type_id;

  const debitAmount =
    debit.debit_note_amount ??
    debit.amount ??
    debit.advance_amount ??
    d.debit_note_amount;
  const debitDesc = debit.description ?? debit.remark ?? "";
  const debitDt = debit.createdAt ?? debit.date_time;
  const waiveAmount =
    waive.waive_off_amount ??
    waive.waive_amount ??
    waive.amount ??
    waive.fuel_amount ??
    waive.debit_note_amount;
  const waiveDesc = waive.description ?? waive.remark ?? waive.reason ?? "";
  const waiveDt = waive.createdAt ?? waive.created_at ?? waive.date_time;
  const advancePeople = resolveDetailPeople(advance);
  const debitPeople = resolveDetailPeople(debit);
  const waivePeople = resolveDetailPeople(waive);
  const hasWaiveOffDetails =
    waive &&
    typeof waive === "object" &&
    Object.keys(waive).length > 0;

  const hasSecondary =
    secondary &&
    typeof secondary === "object" &&
    Object.keys(secondary).length > 0 &&
    (secondary.driver_name || secondary.license_number);

  let verification = "—";
  const fp = advance.fingerprint_signature;
  if (fp != null && fp !== "") {
    verification = typeof fp === "string" && fp.startsWith("http") ? "Signature captured" : String(fp);
  }

  const challan = td.challan_number;
  const challanDisplay =
    challan != null && String(challan).trim() !== "" ? String(challan) : "—";

  const cancelReasonRaw = d.trip_cancel_reason ?? td.trip_cancel_reason ?? "";
  const tripCancelReason =
    cancelReasonRaw != null && String(cancelReasonRaw).trim() !== ""
      ? String(cancelReasonRaw).trim()
      : "";

  const ideal = vehicle.ideal_average ?? vehicle.idealAverage;
  const idealDisplay =
    ideal != null && ideal !== "" && !Number.isNaN(Number(ideal))
      ? `${ideal} average`
      : "—";
  const fuelForm = d.fuel_form_details ?? td.fuel_form_details ?? null;
  const efficiencyDisplay = formatFuelEfficiency(fuelForm);

  const awardAmountRaw = Number(d.award_amount ?? 0);

  return {
    statusLabel: tripStatusLabel(d.status ?? td.status),
    tripCancelReason,
    summaryTotalTripAmount: formatInrDisplay(d.trip_amount ?? td.trip_amount ?? 0),
    summaryAwardBalance: formatInrDisplay(d.award_amount ?? 0),
    isSummaryAwardBalanceNegative: !Number.isNaN(awardAmountRaw) && awardAmountRaw < 0,
    summaryTotalDebit: formatInrDisplay(d.debit_note_amount ?? 0),
    summaryTripAdvance: formatInrDisplay(d.trip_advance_amount ?? 0),
    summaryTotalExpenses: formatInrDisplay(d.total_expense_amount ?? 0),
    awardAmount: formatInrDisplay(d.award_amount ?? 0),
    totalExpenseAmount: formatInrDisplay(d.total_expense_amount ?? 0),
    companyName: company.name ?? "—",
    companyLocation: companyLoc.name ?? "—",
    assignedAccountant: resolvePersonName(td.trip_assigned_accountant ?? d.trip_assigned_accountant),
    startAccountant: resolvePersonName(td.trip_start_accountant ?? d.trip_start_accountant),
    endAccountant: resolvePersonName(td.trip_end_accountant ?? d.trip_end_accountant),
    cancelledBy: resolvePersonName(cancelledBy),
    tripCancelType: resolveCancelType(cancelType),
    tripRefId: td.trip_ref_id != null ? String(td.trip_ref_id) : "—",
    dateTime: formatTripDateTime(td.date_time),
    startDateTime: formatTripDateTime(td.start_date_time),
    tripEndAt: formatTripDateTime(td.trip_end_at),
    miplLocation: mipl.name ?? "—",
    idealAverage: idealDisplay,
    efficiency: efficiencyDisplay,
    tripAmountDetail: formatInrDisplay(td.trip_amount ?? d.trip_amount),
    challanNumber: challanDisplay,
    vehicleLabel: vehicle.name ?? vehicle.registration_number ?? "—",
    advanceAmount: formatInrDisplay(advance.advance_amount ?? d.trip_advance_amount),
    advanceDateTime: formatTripDateTime(advance.createdAt),
    advanceAccountantName: advancePeople.accountantName,
    advanceAccountantId: advancePeople.accountantId,
    advanceDriverName: advancePeople.driverName,
    advanceDriverRefId: advancePeople.driverRefId,
    verificationType: verification,
    debitAmount: debitAmount != null && debitAmount !== "" ? formatInrDisplay(debitAmount) : formatInrDisplay(d.debit_note_amount),
    debitDateTime: formatTripDateTime(debitDt),
    debitDescription: debitDesc.trim() ? debitDesc : "—",
    debitAccountantName: debitPeople.accountantName,
    debitAccountantId: debitPeople.accountantId,
    debitDriverName: debitPeople.driverName,
    debitDriverRefId: debitPeople.driverRefId,
    hasWaiveOffDetails,
    waiveAmount: waiveAmount != null && waiveAmount !== "" ? formatInrDisplay(waiveAmount) : "—",
    waiveDateTime: formatTripDateTime(waiveDt),
    waiveDescription: waiveDesc.trim() ? waiveDesc : "—",
    waiveAccountantName: waivePeople.accountantName,
    waiveAccountantId: waivePeople.accountantId,
    waiveDriverName: waivePeople.driverName,
    waiveDriverRefId: waivePeople.driverRefId,
    primaryDriverName: primary.driver_name ?? "—",
    primaryDriverRefId: primary.driver_ref_id ?? "—",
    primaryLicense: primary.license_number ?? "—",
    hasAdvanceDetails: isNonEmptyObject(advance),
    hasDebitDetails: isNonEmptyObject(debit),
    hasSecondary,
    secondaryDriverName: hasSecondary ? secondary.driver_name ?? "—" : "—",
    secondaryDriverRefId: hasSecondary ? secondary.driver_ref_id ?? "—" : "—",
    secondaryLicense: hasSecondary ? secondary.license_number ?? "—" : "—",
  };
};

const VALID_LIST_TABS = new Set(["pending", "ongoing", "locked", "completed", "cancelled"]);

const ViewTripDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const accountantId = (searchParams.get("accountant_id") || "").trim();
  const tripId = (searchParams.get("trip_id") || "").trim();
  const listStatus = (searchParams.get("status") ?? searchParams.get("tab") ?? "").trim();

  const fallbackBack = VALID_LIST_TABS.has(listStatus)
    ? `/trip-history?status=${encodeURIComponent(listStatus)}`
    : "/trip-history";
  const backToAccountantView = useBackToList(fallbackBack);

  const [loading, setLoading] = useState(Boolean(tripId));
  const [detail, setDetail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewDetail, setViewDetail] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [expenseRows, setExpenseRows] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [expensesTotalPages, setExpensesTotalPages] = useState(1);

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [tripId]);

  useEffect(() => {
    setSubheading("View Trip Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!tripId) {
        setLoading(false);
        setDetail(null);
        notifyError("Trip ID is missing");
        return;
      }
      setLoading(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewTripDetails}?trip_id=${encodeURIComponent(tripId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const normalized = normalizeTripView(response.data);
          setDetail(normalized);
        } else {
          setDetail(null);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load trip details";
          notifyError(errMsg);
        }
      } catch {
        setDetail(null);
        notifyError("Failed to load trip details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tripId]);

  useEffect(() => {
    const loadExpenses = async () => {
      if (!tripId) {
        setExpenseRows([]);
        setExpensesTotal(0);
        setExpensesTotalPages(1);
        setExpensesLoading(false);
        return;
      }
      setExpensesLoading(true);
      try {
        const q = `trip_id=${encodeURIComponent(tripId)}&page=${currentPage}&limit=${itemsPerPage}`;
        const response = await handleApiCall("get", `${endpoint.listOfExpence}?${q}`, {});
        if (response?.data?.code === 200) {
          const payload = response?.data?.data || {};
          const raw =
            payload.trip_expence_data ??
            payload.trip_expense_data ??
            payload.trip_expence ??
            [];
          const list = Array.isArray(raw) ? raw : [];
          setExpenseRows(list.map(mapExpenseRow));
          setExpensesTotal(typeof payload.total === "number" ? payload.total : list.length);
          setExpensesTotalPages(
            Math.max(1, typeof payload.total_pages === "number" ? payload.total_pages : 1)
          );
        } else {
          setExpenseRows([]);
          setExpensesTotal(0);
          setExpensesTotalPages(1);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load expenses";
          notifyError(errMsg);
        }
      } catch {
        setExpenseRows([]);
        setExpensesTotal(0);
        setExpensesTotalPages(1);
        notifyError("Failed to load expenses");
      } finally {
        setExpensesLoading(false);
      }
    };
    loadExpenses();
  }, [tripId, currentPage, itemsPerPage]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentDrivers = expenseRows;
  const totalPages = expensesTotalPages;
  const showExpenseTransitionTable =
    detail?.statusLabel === "Locked" || detail?.statusLabel === "Completed";

  const closeModal = () => {
    setViewDetail(false);
    setViewReceipt(false);
  };

  const Card = ({ children }) => (
    <div className="bg-white rounded-2xl border md:p-6 px-3 py-2 max-[767px]:text-sm md:mb-4 mb-2 font-UbuntuMedium">{children}</div>
  );

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-1">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );

  const SummaryStatCard = ({ label, value, valueClassName }) => (
    <div className="rounded-xl border border-primary/30 bg-primary/[0.07] md:px-4 md:py-5 !px-3 !py-4 shadow-sm">
      <div
        className={`text-xl md:text-2xl font-UbuntuMedium tabular-nums leading-tight ${
          valueClassName ?? "text-[#000053]"
        }`}
      >
        {value}
      </div>
      <div className="mt-2 text-sm text-gray-500 font-UbuntuRegular leading-snug">{label}</div>
    </div>
  );

  return (
    <>
      <div className="md:mt-7 !mt-4">
        <div className="flex md:!mb-5 !mb-4 md:items-start items-center">
          <div>
            <h3 className="md:text-xl font-UbuntuMedium !my-0 flex items-center gap-1.5 flex-wrap">
              <span>Status:</span>
              <span className="font-UbuntuBold text-[#000053]">
                {loading ? "…" : detail?.statusLabel ?? "—"}
              </span>
            </h3>
            {!loading && detail?.tripCancelReason ? (
              <p className="mb-0 mt-1 text-sm font-UbuntuMedium text-red-600">
                {detail.tripCancelReason}
              </p>
            ) : null}
          </div>
          <Link to={backToAccountantView} className="btn btn-primary ml-auto">
            Back
          </Link>
        </div>

        {!loading && detail && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 md:gap-4 gap-3 mb-6">
            <SummaryStatCard label="Total Trip Amount" value={detail.summaryTotalTripAmount} />
            <SummaryStatCard
              label="Award Balance"
              value={detail.summaryAwardBalance}
              valueClassName={detail.isSummaryAwardBalanceNegative ? "text-danger" : undefined}
            />
            <SummaryStatCard label="Total Debit" value={detail.summaryTotalDebit} />
            <SummaryStatCard label="Trip Advance" value={detail.summaryTripAdvance} />
            <SummaryStatCard label="Total Expenses" value={detail.summaryTotalExpenses} />
          </div>
        )}

        {loading && <p className="text-gray-500 py-4">Loading...</p>}

        {!loading && !tripId && (
          <p className="text-gray-500 py-4 mb-0">No trip selected.</p>
        )}

        {!loading && tripId && !detail && (
          <p className="text-gray-500 py-4 mb-0">Unable to load trip details.</p>
        )}

        {!loading && detail && (
          <div className="">
            <div className="md:bg-white md:shadow-sm rounded-md md:p-6 mb-4 max-[767px]:border p-2">
              <h3 className="md:text-lg text-base font-UbuntuMedium md:mb-3 mb-2">Trip Details</h3>
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12">
                  <div>
                    <Row label="Company" value={detail.companyName} />
                    <Row label="Company Location" value={detail.companyLocation} />
                    <Row label="Trip Assigned by" value={detail.assignedAccountant} />
                    <Row label="Trip Started by" value={detail.startAccountant} />
                    <Row label="Trip Ended By" value={detail.endAccountant} />
                  </div>
                  <div>
                    <Row label="Trip ID" value={detail.tripRefId} />
                    <Row label="Date & Time" value={detail.dateTime} />
                    <Row label="Start Date & Time" value={detail.startDateTime} />
                    {detail.tripEndAt !== "—" && (
                      <Row label="Trip End" value={detail.tripEndAt} />
                    )}
                    {detail.tripCancelType !== "—" && (
                      <Row label="Trip Cancel Type" value={detail.tripCancelType} />
                    )}
                    {detail.cancelledBy !== "—" && (
                      <Row label="Trip Cancelled by" value={detail.cancelledBy} />
                    )}
                  </div>
                </div>
              </Card>
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12">
                  <div>
                    <Row label="MIPL Location" value={detail.miplLocation} />
                    <Row label="Ideal Average" value={detail.idealAverage} />
                    <Row label="Efficiency" value={detail.efficiency} />
                  </div>
                  <div>
                    {/* <Row label="Trip Amount" value={detail.tripAmountDetail} /> */}
                    <Row label="Award Amount" value={detail.awardAmount} />
                    <Row label="Total Expense Amount" value={detail.totalExpenseAmount} />
                    <Row label="Challan Number" value={detail.challanNumber} />
                    <Row label="Vehicle Number" value={detail.vehicleLabel} />
                  </div>
                </div>
              </Card>
            </div>

            <h3 className="md:text-lg text-base font-UbuntuMedium !mt-4 md:mt-6 mb-2 md:mb-3">Trip Advance Details</h3>
            <Card>
              {detail.hasAdvanceDetails ? (
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12">
                  <div>
                    <Row label="Amount Provided" value={detail.advanceAmount} />
                    <Row label="Date & Time" value={detail.advanceDateTime} />
                    {detail.advanceAccountantName !== "—" && (
                      <Row label="Accountant Name" value={detail.advanceAccountantName} />
                    )}
                    {detail.advanceAccountantId !== "—" && (
                      <Row label="Accountant ID" value={detail.advanceAccountantId} />
                    )}
                  </div>
                  <div>
                    <Row label="Verification Type" value={detail.verificationType} />
                    {detail.advanceDriverName !== "—" && (
                      <Row label="Driver Name" value={detail.advanceDriverName} />
                    )}
                    {detail.advanceDriverRefId !== "—" && (
                      <Row label="Driver ID" value={detail.advanceDriverRefId} />
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No advance details.</p>
              )}
            </Card>

            <h3 className="md:text-lg text-base font-UbuntuMedium !mt-4 md:mt-6 mb-2 md:mb-3">Debit Note Payment Details</h3>
            <Card>
              {detail.hasDebitDetails ? (
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12">
                  <div>
                    <Row label="Amount" value={detail.debitAmount} />
                    <Row label="Date & Time" value={detail.debitDateTime} />
                    {detail.debitAccountantName !== "—" && (
                      <Row label="Accountant Name" value={detail.debitAccountantName} />
                    )}
                    {detail.debitAccountantId !== "—" && (
                      <Row label="Accountant ID" value={detail.debitAccountantId} />
                    )}
                  </div>
                  <div>
                    {detail.debitDriverName !== "—" && (
                      <Row label="Driver Name" value={detail.debitDriverName} />
                    )}
                    {detail.debitDriverRefId !== "—" && (
                      <Row label="Driver ID" value={detail.debitDriverRefId} />
                    )}
                    <div className="text-gray-400">Description</div>
                    <p className="text-gray-800 mt-1">{detail.debitDescription}</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No debit note details.</p>
              )}
            </Card>

            {detail.hasWaiveOffDetails ? (
              <>
                <h3 className="md:text-lg text-base font-UbuntuMedium !mt-4 md:mt-6 mb-2 md:mb-3">Waive Off Details</h3>
                <Card>
                  <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12">
                    <div>
                      <Row label="Amount" value={detail.waiveAmount} />
                      <Row label="Date & Time" value={detail.waiveDateTime} />
                      {detail.waiveAccountantName !== "—" && (
                        <Row label="Accountant Name" value={detail.waiveAccountantName} />
                      )}
                      {detail.waiveAccountantId !== "—" && (
                        <Row label="Accountant ID" value={detail.waiveAccountantId} />
                      )}
                    </div>
                    <div>
                      {detail.waiveDriverName !== "—" && (
                        <Row label="Driver Name" value={detail.waiveDriverName} />
                      )}
                      {detail.waiveDriverRefId !== "—" && (
                        <Row label="Driver ID" value={detail.waiveDriverRefId} />
                      )}
                      <div className="text-gray-400">Description</div>
                      <p className="text-gray-800 mt-1">{detail.waiveDescription}</p>
                    </div>
                  </div>
                </Card>
              </>
            ) : null}
            <h3 className="md:text-lg text-base font-UbuntuMedium !mt-4 md:mt-6 mb-2 md:mb-3">Driver Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12">
              <Card>
                <div className="md:text-lg text-base font-UbuntuMedium md:mb-3 md:-mt-2">Primary Driver</div>
                <div>
                  <Row label="Driver Name" value={detail.primaryDriverName} />
                  <Row label="Driver ID" value={detail.primaryDriverRefId} />
                  <Row label="License Number" value={detail.primaryLicense} />
                </div>
              </Card>
              <Card>
                <div className="md:text-lg text-base font-UbuntuMedium md:mb-3 md:-mt-2">Secondary Driver</div>
                <div>
                  {detail.hasSecondary ? (
                    <>
                      <Row label="Driver Name" value={detail.secondaryDriverName} />
                      <Row label="Driver ID" value={detail.secondaryDriverRefId} />
                      <Row label="License Number" value={detail.secondaryLicense} />
                    </>
                  ) : (
                    <p className="text-gray-500 text-sm">No secondary driver.</p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {!loading && detail && showExpenseTransitionTable && (
          <>
            <h3 className="md:text-lg text-base font-UbuntuMedium !mt-4 md:mt-6 mb-2 md:mb-3">All Expenses Payment Transition Details</h3>
            <div className="common-table-wrapper m-0 shadow-sm">
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Amount Spent</th>
                      <th>Accountant Name</th>
                      <th>Description</th>
                      <th>Date & Time</th>
                      <th>Receipt Image</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expensesLoading && (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-gray-500">
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!expensesLoading && currentDrivers.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-gray-500">
                          No expense records
                        </td>
                      </tr>
                    )}
                    {!expensesLoading &&
                      currentDrivers.map((driver, index) => (
                      <tr key={driver.id}>
                        <td>{indexOfFirst + index + 1}</td>
                        <td>{driver.amountSpent}</td>
                        <td>{driver.accountantName}</td>
                        <td>{driver.description}</td>
                        <td>{driver.dateTime}</td>
                        <td>
                          {driver.receipt ? (
                            <img
                              src={driver.receipt}
                              alt=""
                              className="max-w-24 border max-h-10 mx-auto cursor-pointer"
                              onClick={() => {
                                setSelectedImage(driver.receipt);
                                setViewReceipt(true);
                              }}
                            />
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center justify-center gap-2">
                            <img
                              src={EyeIcon}
                              className="h-7 cursor-pointer"
                              alt="View"
                              onClick={() => {
                                setModalDescription(driver.rawDescription);
                                setViewDetail(true);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex gap-x-4 items-center mt-4">
              <select
                className="p-2 shadow-sm outline-none max-[767px]:text-xs"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
              <div className="text-xs md:text-sm text-gray-500 ml-auto">
                {expensesTotal} records found
              </div>
              <div className="flex items-center gap-2 md:pr-8">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1 || expensesTotal === 0 || expensesLoading}
                  className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                    currentPage === 1 ? "" : "cursor-pointer"
                  }`}
                >
                  <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
                </button>
                <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                <span className="text-sm text-gray-600 text-nowrap">of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={
                    currentPage === totalPages || expensesTotal === 0 || expensesLoading
                  }
                  className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                    currentPage === totalPages ? "" : "cursor-pointer"
                  }`}
                >
                  <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {viewDetail && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white py-6 px-8 rounded-xl max-w-screen-md text-center font-UbuntuRegular">
            <p className="mb-4 text-xl font-UbuntuMedium">View Expense Description</p>
            <p className="text-left">{modalDescription}</p>
            <div className="flex justify-center gap-4 mt-6 mb-2">
              <button type="button" className="btn btn-danger" onClick={closeModal}>
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {viewReceipt && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white py-6 px-8 rounded-xl max-w-screen-md text-center font-UbuntuRegular">
            <p className="mb-4 text-xl font-UbuntuMedium">Receipt Image</p>
            <img src={selectedImage} className="max-h-[400px] mx-auto" alt="" />
            <div className="flex justify-center gap-4 mt-6 mb-2">
              <button type="button" className="btn btn-danger" onClick={closeModal}>
              Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewTripDetails;
